import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  Building2,
  CheckCircle2,
  ChevronRight,
  CircleUserRound,
  Database,
  Download,
  FileClock,
  FileText,
  HeartPulse,
  Hospital,
  Image,
  LayoutDashboard,
  LoaderCircle,
  LogOut,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Upload,
  Users,
  XCircle,
} from "lucide-react";

const navigation = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["patients", "Patients", CircleUserRound],
  ["upload", "New Upload", Upload],
  ["processing", "Processing", FileClock],
  ["downloads", "Downloads", Download],
  ["activity", "Activity", Activity],
  ["users", "Users", Users],
  ["hospitals", "Hospitals & Programs", Building2],
  ["health", "System Health", HeartPulse],
];

const copdClinicalSections = [
  ["Symptoms", [
    ["coughSymptoms", "Cough"], ["dyspneaGradeSymptoms", "Dyspnea grade"], ["expectoration", "Expectoration"],
    ["hemoptysis", "Hemoptysis"], ["chestTightness", "Chest tightness"],
  ]],
  ["Comorbidities & history", [
    ["t2dm", "T2DM"], ["htn", "HTN"], ["cad", "CAD"], ["tb", "TB"], ["covid", "COVID"],
    ["childhoodPneumonia", "Childhood pneumonia"], ["smoking", "Smoking"], ["exposure", "Exposure"],
  ]],
  ["Laboratory & identity", [
    ["hbHaemoglobin", "Hb Haemoglobin"], ["tlc", "TLC"], ["crNo", "CR No"], ["name", "Name"],
    ["age", "Age"], ["sex", "Sex"],
  ]],
  ["Clinical assessment & imaging", [
    ["coughAssessment", "Cough"], ["dyspneaGradeAssessment", "Dyspnea grade"], ["fev1Fvc", "FEV1/FVC"],
    ["emphysema", "Emphysema"], ["bullaeCyst", "Bullae, Cyst"], ["nodulesGgo", "Nodules, GGO"],
    ["mediastinalLn", "Mediastinal LN"], ["pleuralEffusion", "Pleural effusion"], ["pneumothorax", "Pneumothorax"],
  ]],
  ["Cardiac & autoimmune investigations", [
    ["ecg", "ECG"], ["ana", "ANA"], ["ena", "ENA"], ["ra", "RA"], ["msa", "MSA"],
    ["serumCalcium", "S. calcium"], ["serumAce", "S. ACE"],
  ]],
];

function badge(status) {
  return `platform-badge status-${String(status || "unknown").toLowerCase()}`;
}

function formatDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function usePlatformApi() {
  const [identity, setIdentity] = useState(null);
  const csrfTokenRef = useRef("");
  const request = useCallback(
    async (path, options = {}) => {
      const send = (csrfToken) => {
        const headers = {
          ...(options.body ? { "Content-Type": "application/json" } : {}),
          ...(options.headers || {}),
        };
        if (options.method && !["GET", "HEAD", "OPTIONS"].includes(options.method.toUpperCase()) && csrfToken)
          headers["X-CSRF-Token"] = csrfToken;
        return fetch(`/api/v2${path}`, { credentials: "same-origin", ...options, headers });
      };
      let response = await send(csrfTokenRef.current);
      if (response.status === 401) {
        window.location.href = "/login";
        throw new Error("Authentication required.");
      }
      let body =
        response.status === 204
          ? null
          : await response.json().catch(() => null);
      if (response.status === 403 && body?.error?.code === "CSRF_INVALID") {
        const sessionResponse = await fetch("/api/v2/me", { credentials: "same-origin", cache: "no-store" });
        const sessionIdentity = await sessionResponse.json().catch(() => null);
        if (sessionResponse.ok && sessionIdentity?.csrfToken) {
          csrfTokenRef.current = sessionIdentity.csrfToken;
          setIdentity(sessionIdentity);
          response = await send(sessionIdentity.csrfToken);
          body = response.status === 204 ? null : await response.json().catch(() => null);
        }
      }
      if (!response.ok)
        throw new Error(
          body?.error?.message || `Request failed (${response.status}).`,
        );
      return body;
    },
    [],
  );
  useEffect(() => {
    fetch("/api/v2/me", { credentials: "same-origin" })
      .then(async (response) => {
        if (response.status === 401) return (window.location.href = "/login");
        const body = await response.json();
        if (!response.ok)
          throw new Error(body?.error?.message || "Unable to load session.");
        csrfTokenRef.current = body.csrfToken || "";
        setIdentity(body);
      })
      .catch((error) => setIdentity({ error: error.message }));
  }, []);
  return { identity, request };
}

function PageHeader({ eyebrow, title, description, action }) {
  return (
    <header className="platform-page-header">
      <div>
        <span>{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action}
    </header>
  );
}

function Loading() {
  return (
    <div className="platform-loading">
      <LoaderCircle className="spin" /> Loading secure workspace…
    </div>
  );
}
function ErrorNotice({ message }) {
  return message ? (
    <div className="platform-error">
      <XCircle size={17} />
      {message}
    </div>
  ) : null;
}

function PatientTable({ records, onOpen }) {
  return (
    <div className="platform-table-wrap">
      <table className="platform-table">
        <thead>
          <tr>
            <th>Anonymous patient</th>
            <th>Hospital</th>
            <th>Program</th>
            <th>CT</th>
            <th>X-ray</th>
            <th>Documents</th>
            <th>Status</th>
            <th>Updated</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {records.map((row) => (
            <tr key={row.id} onDoubleClick={() => onOpen(row.id)}>
              <td>
                <strong>{row.anonymousPatientId}</strong>
                <small>{row.originalFolderName || "No folder label"}</small>
              </td>
              <td>{row.hospitalCode}</td>
              <td>{row.programCode}</td>
              <td>{row.ctCount}</td>
              <td>{row.xrayCount}</td>
              <td>{row.documentCount}</td>
              <td>
                <span className={badge(row.status)}>{row.status}</span>
              </td>
              <td>{formatDate(row.updatedAt)}</td>
              <td>
                <button
                  className="icon-action"
                  onClick={() => onOpen(row.id)}
                  aria-label={`Open ${row.anonymousPatientId}`}
                >
                  <ChevronRight size={17} />
                </button>
              </td>
            </tr>
          ))}
          {!records.length && (
            <tr>
              <td colSpan="9" className="platform-empty">
                No patients match this view.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Dashboard({ request, openPatient, navigate, compactNavigation }) {
  const [data, setData] = useState();
  const [analytics, setAnalytics] = useState();
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  useEffect(() => {
    Promise.all([request("/patients?limit=10"), request("/analytics")])
      .then(([patients, metrics]) => {
        setData(patients);
        setAnalytics(metrics);
      })
      .catch((e) => setError(e.message));
  }, [request]);
  if ((!data || !analytics) && !error) return <Loading />;
  const rows = (data?.records || []).filter(
    (row) =>
      (!query ||
        `${row.anonymousPatientId} ${row.originalFolderName || ""}`
          .toLowerCase()
          .includes(query.toLowerCase())) &&
      (!status || row.status === status),
  );
  const summary = analytics?.summary || {
    patients: 0,
    ct: 0,
    xray: 0,
    documents: 0,
    pending: 0,
  };
  return (
    <div className="hospital-dashboard">
      <PageHeader
        eyebrow=""
        title="Welcome back"
        description="Manage and track your study uploads"
        action={
          compactNavigation ? (
            <div className="header-actions dashboard-utilities">
              <button onClick={() => navigate("processing")}>Processing</button>
              <button onClick={() => navigate("downloads")}>Downloads</button>
              <button onClick={() => navigate("activity")}>Activity</button>
              <button onClick={() => navigate("users")}>Users</button>
            </div>
          ) : null
        }
      />
      <ErrorNotice message={error} />
      <div className="platform-metrics">
        {[
          ["Total Patients", summary.patients, CircleUserRound],
          ["CT Studies", summary.ct, Database],
          ["X-Ray Studies", summary.xray, Image],
          ["Reports Uploaded", summary.documents, FileText],
        ].map(([label, value, Icon], index) => (
          <article className={`metric-${index + 1}`} key={label}>
            <div className="metric-icon">
              <Icon size={25} />
            </div>
            <span>{label}</span>
            <strong>{value.toLocaleString()}</strong>
            <button onClick={() => navigate("patients")}>
              View all <ChevronRight size={15} />
            </button>
          </article>
        ))}
      </div>
      <div className="dashboard-filterbar">
        <label className="platform-search">
          <Search size={18} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by Patient ID or Folder Name..."
          />
        </label>
        <label>
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All Status</option>
            <option>ACTIVE</option>
            <option>PROCESSING</option>
            <option>READY</option>
            <option>FAILED</option>
          </select>
        </label>
        <button className="primary-button" onClick={() => navigate("upload")}>
          <Upload size={17} />
          Upload Study
        </button>
      </div>
      <section className="platform-section dashboard-patient-list">
        <PatientTable records={rows} onOpen={openPatient} />
      </section>
      <section className="platform-section program-study-summary">
        <div className="section-heading">
          <div><span>Study overview</span><h2>Hospital and program-wise data</h2></div>
        </div>
        <div className="platform-table-wrap">
          <table className="platform-table">
            <thead><tr><th>Hospital</th><th>Program</th><th>Patients</th><th>CT</th><th>X-ray</th><th>Other imaging</th><th>Instances</th><th>Documents</th></tr></thead>
            <tbody>
              {(analytics?.programs || []).map((row) => <tr key={`${row.hospitalId}:${row.programId}`}><td>{row.hospitalCode}</td><td><strong>{row.programCode}</strong></td><td>{row.patients}</td><td>{row.ct}</td><td>{row.xray}</td><td>{row.other}</td><td>{Number(row.instances).toLocaleString()}</td><td>{row.documents}</td></tr>)}
              {!(analytics?.programs || []).length && <tr><td colSpan="8" className="platform-empty">No program data is available.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Patients({ request, openPatient }) {
  const [query, setQuery] = useState("");
  const [modality, setModality] = useState("");
  const [status, setStatus] = useState("");
  const [hospitalId, setHospitalId] = useState("");
  const [programId, setProgramId] = useState("");
  const [hospitals, setHospitals] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [data, setData] = useState({ records: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    request("/hospitals").then((body) => setHospitals(body.records)).catch((e) => setError(e.message));
  }, [request]);
  useEffect(() => { setPage(1); }, [query, modality, status, hospitalId, programId]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      request(
        `/patients?limit=${pageSize}&offset=${(page - 1) * pageSize}&search=${encodeURIComponent(query)}&modality=${encodeURIComponent(modality)}&status=${encodeURIComponent(status)}&hospitalId=${encodeURIComponent(hospitalId)}&programId=${encodeURIComponent(programId)}`,
      )
        .then(setData)
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(timer);
  }, [query, modality, status, hospitalId, programId, page, request]);
  const availablePrograms = hospitals.filter((hospital) => !hospitalId || hospital.id === hospitalId).flatMap((hospital) => hospital.programs || []).filter((program, index, all) => all.findIndex((item) => item.id === program.id) === index);
  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));
  return (
    <>
      <PageHeader
        eyebrow="Research registry"
        title="Patients"
        description="One anonymous profile for imaging, reports, documents, and activity."
      />
      <div className="platform-toolbar">
        <label className="platform-search">
          <Search size={17} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search anonymous ID or authorized folder label"
          />
        </label>
        <select aria-label="Hospital filter" value={hospitalId} onChange={(e) => { setHospitalId(e.target.value); setProgramId(""); }}>
          <option value="">All hospitals</option>
          {hospitals.map((hospital) => <option key={hospital.id} value={hospital.id}>{hospital.code}</option>)}
        </select>
        <select aria-label="Program filter" value={programId} onChange={(e) => setProgramId(e.target.value)}>
          <option value="">All programs</option>
          {availablePrograms.map((program) => <option key={program.id} value={program.id}>{program.code}</option>)}
        </select>
        <select
          aria-label="Modality filter"
          value={modality}
          onChange={(e) => setModality(e.target.value)}
        >
          <option value="">All imaging</option>
          <option>CT</option>
          <option value="XRAY">X-ray</option>
        </select>
        <select
          aria-label="Status filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          <option>ACTIVE</option>
          <option>PROCESSING</option>
          <option>READY</option>
          <option>FAILED</option>
        </select>
        <span>{data.total || 0} patients</span>
      </div>
      <ErrorNotice message={error} />
      {loading ? (
        <Loading />
      ) : (
        <>
          <PatientTable records={data.records} onOpen={openPatient} />
          <nav className="platform-pagination" aria-label="Patient pages">
            <button className="secondary-button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</button>
            <span>Page {page} of {totalPages} · {data.total || 0} patients</span>
            <button className="secondary-button" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>Next</button>
          </nav>
        </>
      )}
    </>
  );
}

function PatientProfile({ id, request, onBack, onUpload }) {
  const [data, setData] = useState();
  const [error, setError] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [download, setDownload] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [inlineUploadType, setInlineUploadType] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [clinicalEditing, setClinicalEditing] = useState(false);
  const [clinicalDraft, setClinicalDraft] = useState({});
  const [clinicalSaving, setClinicalSaving] = useState(false);
  const [clinicalError, setClinicalError] = useState("");
  const [viewerLoadingId, setViewerLoadingId] = useState(null);
  const load = useCallback(
    () =>
      request(`/patients/${id}`)
        .then(setData)
        .catch((e) => setError(e.message)),
    [id, request],
  );
  useEffect(() => {
    void load();
  }, [load]);
  if (error && !data)
    return (
      <>
        <button className="text-action" onClick={onBack}>
          ← Patients
        </button>
        <ErrorNotice message={error} />
      </>
    );
  if (!data) return <Loading />;
  const { patient, studies, documents } = data;
  const resources = [
    ...studies
      .filter((r) => r.status === "READY")
      .map((r) => ({
        type: "STUDY",
        id: r.id,
        label: `${r.studyType} · ${r.instanceCount} instances`,
      })),
    ...documents
      .filter((r) => r.status === "READY")
      .map((r) => ({
        type: "DOCUMENT",
        id: r.id,
        label: `${r.documentType} · ${r.filename}`,
      })),
  ];
  const generate = async () => {
    try {
      const created = await request("/downloads", {
        method: "POST",
        headers: { "Idempotency-Key": clientRequestId() },
        body: JSON.stringify({
          patientId: patient.id,
          items: resources
            .filter((r) => selected.includes(`${r.type}:${r.id}`))
            .map(({ type, id }) => ({ type, id })),
        }),
      });
      setDownload(created);
      const poll = async () => {
        const status = await request(`/downloads/${created.id}`);
        setDownload(status);
        if (["QUEUED", "PROCESSING"].includes(status.status))
          setTimeout(poll, 2000);
      };
      poll();
    } catch (e) {
      setError(e.message);
    }
  };
  const remove = async (type, resource) => {
    const label =
      type === "studies" ? `${resource.studyType} study` : resource.filename;
    if (
      !window.confirm(
        `Delete ${label}? This removes it from normal views but preserves its audit history.`,
      )
    )
      return;
    try {
      await request(`/${type}/${resource.id}`, { method: "DELETE" });
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const removePatient = async () => {
    if (
      !window.confirm(
        `Delete patient ${patient.anonymousPatientId} and all attached studies and clinical files? This action removes them from normal views while preserving the audit history.`,
      )
    )
      return;
    try {
      await request(`/patients/${patient.id}`, { method: "DELETE" });
      onBack();
    } catch (e) {
      setError(e.message);
    }
  };
  const editClinicalInformation = () => {
    setClinicalDraft(data.clinicalInformation?.information || {});
    setClinicalError("");
    setClinicalEditing(true);
  };
  const saveClinicalInformation = async () => {
    try {
      setClinicalSaving(true);
      setClinicalError("");
      await request(`/patients/${patient.id}/copd-clinical-information`, {
        method: "PUT",
        body: JSON.stringify({ information: clinicalDraft }),
      });
      await load();
      setClinicalEditing(false);
    } catch (e) {
      setClinicalError(e.message);
    } finally {
      setClinicalSaving(false);
    }
  };
  const openStudyViewer = async (study) => {
    const viewerTab = window.open("", "_blank");
    if (!viewerTab) { setError("Allow pop-ups for this portal to open the study viewer."); return; }
    viewerTab.opener = null;
    viewerTab.document.title = "Preparing study viewer";
    viewerTab.document.body.innerHTML = "<main style='font:16px system-ui;max-width:680px;margin:80px auto;padding:32px;border:1px solid #dbe4ee;border-radius:12px'><h1 style='font-size:24px'>Opening study viewer</h1><p id='viewer-status'>Loading the first images...</p><p style='color:#617087'>This study has priority and will open automatically as soon as its first image is ready.</p></main>";
    const statusNode = viewerTab.document.getElementById("viewer-status");
    const showViewerStatus = (result) => {
      if (!statusNode) return;
      const indexed = result.progress && typeof result.progress === "object" ? Number(result.progress.indexed) : NaN;
      if (Number.isFinite(indexed)) statusNode.textContent = `Indexing DICOM images: ${indexed} of ${study.instanceCount}.`;
      else if (result.status === "active") statusNode.textContent = "Indexing DICOM images...";
      else statusNode.textContent = "Loading the first images...";
    };
    setViewerLoadingId(study.id);
    try {
      let result = await request(`/studies/${study.id}/external-viewer-link`, { method: "POST", body: "{}" });
      showViewerStatus(result);
      for (let attempt = 0; result.status !== "ready" && attempt < 150; attempt += 1) {
        if (viewerTab.closed) throw new Error("The viewer tab was closed. Indexing will continue in the background.");
        await new Promise((resolve) => setTimeout(resolve, 500));
        result = await request(`/studies/${study.id}/external-viewer-status`);
        showViewerStatus(result);
      }
      if (result.status !== "ready" || !result.viewerUrl) throw new Error("The external viewer is still indexing this study. Close this tab and try again later.");
      viewerTab.location.replace(result.viewerUrl);
    } catch (e) {
      if (!viewerTab.closed && statusNode) {
        statusNode.textContent = e.message;
        viewerTab.document.title = "Study viewer not ready";
      }
      setError(e.message);
    } finally { setViewerLoadingId(null); }
  };
  return (
    <>
      <button className="text-action" onClick={onBack}>
        ← Patients
      </button>
      <PageHeader
        eyebrow={`${patient.hospital.name} · ${patient.program.code}`}
        title={patient.anonymousPatientId}
        description={`Created ${formatDate(patient.createdAt)} · ${patient.originalFolderName || "No folder label"}`}
        action={
          <div className="header-actions">
            <span className={badge(patient.status)}>{patient.status}</span>
            <button
              className="secondary-button"
              onClick={() => setAddOpen(true)}
            >
              <Plus size={16} />
              Add data
            </button>
            <button
              className="primary-button"
              disabled={!resources.length}
              onClick={() => {
                setSelected(resources.map((r) => `${r.type}:${r.id}`));
                setDownloadOpen(true);
              }}
            >
              <Download size={16} />
              Download
            </button>
            <button className="secondary-button danger" onClick={removePatient}>
              Delete patient
            </button>
          </div>
        }
      />
      <ErrorNotice message={error} />
      {patient.program.code === "COPD" && (
        <section className="platform-section copd-clinical-section">
          <div className="section-heading">
            <div>
              <span>COPD profile</span>
              <h2>Clinical information</h2>
            </div>
            {!clinicalEditing ? (
              <button className="secondary-button" onClick={editClinicalInformation}>Edit information</button>
            ) : (
              <div className="header-actions">
                <button className="secondary-button" disabled={clinicalSaving} onClick={() => setClinicalEditing(false)}>Cancel</button>
                <button className="primary-button" disabled={clinicalSaving} onClick={saveClinicalInformation}>
                  {clinicalSaving ? <LoaderCircle className="spin" size={16} /> : null} Save information
                </button>
              </div>
            )}
          </div>
          <ErrorNotice message={clinicalError} />
          <div className="copd-clinical-groups">
            {copdClinicalSections.map(([section, fields]) => (
              <section key={section} className="copd-clinical-group">
                <h3>{section}</h3>
                <div className="copd-clinical-grid">
                  {fields.map(([field, label]) => (
                    <label key={field}>
                      <span>{label}</span>
                      {clinicalEditing ? (
                        <textarea value={clinicalDraft[field] || ""} onChange={(event) => setClinicalDraft((current) => ({ ...current, [field]: event.target.value }))} />
                      ) : (
                        <p>{data.clinicalInformation?.information?.[field] || "Not entered"}</p>
                      )}
                    </label>
                  ))}
                </div>
              </section>
            ))}
          </div>
          {data.clinicalInformation?.updatedAt && !clinicalEditing && <small className="clinical-updated">Last updated {formatDate(data.clinicalInformation.updatedAt)}</small>}
        </section>
      )}
      <div className="profile-grid">
        <section className="platform-section">
          <div className="section-heading">
            <div>
              <span>Imaging</span>
              <h2>Studies</h2>
            </div>
          </div>
          <div className="resource-list">
            {studies.map((study) => (
              <article key={study.id}>
                <div className="resource-icon">
                  <Image />
                </div>
                <div>
                  <strong>
                    {study.studyType} · {study.modality}
                  </strong>
                  <span>
                    {study.instanceCount} instances · {study.seriesCount} series
                  </span>
                  <small>{formatDate(study.updatedAt)}</small>
                </div>
                <span className={badge(study.status)}>{study.status}</span>
                <div className="resource-actions">
                  {study.status === "READY" && (
                    <button
                      className="secondary-button"
                      disabled={viewerLoadingId === study.id}
                      onClick={() => openStudyViewer(study)}
                    >
                      {viewerLoadingId === study.id ? <LoaderCircle className="spin" size={15} /> : null}
                      Open viewer
                    </button>
                  )}
                  <button
                    className="secondary-button"
                    onClick={() =>
                      onUpload({
                        hospitalId: patient.hospital.id,
                        programId: patient.program.id,
                        patientId: patient.id,
                        patientMode: "existing",
                        uploadType: study.studyType,
                        replacesId: study.id,
                      })
                    }
                  >
                    Replace
                  </button>
                  <button
                    className="secondary-button danger"
                    onClick={() => remove("studies", study)}
                  >
                    Delete
                  </button>
                </div>
              </article>
            ))}
            {!studies.length && (
              <p className="platform-empty">No imaging studies.</p>
            )}
          </div>
        </section>
        <section className="platform-section">
          <div className="section-heading">
            <div>
              <span>Clinical files</span>
              <h2>Reports & documents</h2>
            </div>
          </div>
          <div className="resource-list">
            {documents.map((document) => (
              <article key={document.id}>
                <div className="resource-icon">
                  <FileText />
                </div>
                <div>
                  <strong>{document.documentType}</strong>
                  <span>{document.filename}</span>
                  <small>{formatDate(document.updatedAt)}</small>
                </div>
                <span className={badge(document.status)}>
                  {document.status}
                </span>
                <div className="resource-actions">
                  {document.status === "READY" && (
                    <button
                      className="secondary-button"
                      onClick={() => {
                        setPreviewDocument(document);
                      }}
                    >
                      View file
                    </button>
                  )}
                  <button
                    className="secondary-button"
                    onClick={() =>
                      onUpload({
                        hospitalId: patient.hospital.id,
                        programId: patient.program.id,
                        patientId: patient.id,
                        patientMode: "existing",
                        uploadType:
                          document.documentType === "SUPPORTING"
                            ? "DOCUMENT"
                            : document.documentType,
                        replacesId: document.id,
                      })
                    }
                  >
                    Replace
                  </button>
                  <button
                    className="secondary-button danger"
                    onClick={() => remove("documents", document)}
                  >
                    Delete
                  </button>
                </div>
              </article>
            ))}
            {!documents.length && (
              <p className="platform-empty">No reports or documents.</p>
            )}
          </div>
        </section>
      </div>
      {addOpen && (
        <div className="platform-modal-backdrop">
          <section
            className={`platform-modal add-data-dialog ${inlineUploadType ? "file-selected" : ""}`}
          >
            <div className="section-heading">
              <div>
                <span>Patient data</span>
                <h2>
                  {inlineUploadType
                    ? "Attach files to this patient"
                    : "What do you want to upload?"}
                </h2>
              </div>
              <button
                className="icon-action"
                onClick={() => {
                  setAddOpen(false);
                  setInlineUploadType(null);
                }}
              >
                <XCircle />
              </button>
            </div>
            {inlineUploadType ? (
              <>
                <button
                  className="text-action"
                  onClick={() => setInlineUploadType(null)}
                >
                  ← Change data type
                </button>
                <UploadWizard
                  inline
                  request={request}
                  hospitals={[
                    {
                      id: patient.hospital.id,
                      name: patient.hospital.name,
                      programs: [patient.program],
                    },
                  ]}
                  preset={{
                    hospitalId: patient.hospital.id,
                    programId: patient.program.id,
                    patientId: patient.id,
                    patientMode: "existing",
                    uploadType: inlineUploadType,
                  }}
                  onComplete={() => {
                    setAddOpen(false);
                    setInlineUploadType(null);
                    load();
                  }}
                />
              </>
            ) : (
              <div className="upload-type-grid">
                {[
                  ["CT study", "CT", Database],
                  ["X-ray study", "XRAY", Image],
                  ["Other DICOM", "IMAGING", Upload],
                  ["Radiology report", "RADIOLOGY", FileText],
                  ["Histopathology report", "HISTOPATHOLOGY", FileText],
                  ["Supporting file", "DOCUMENT", FileText],
                ].map(([label, type, Icon]) => (
                  <button key={type} onClick={() => setInlineUploadType(type)}>
                    <Icon />
                    <strong>{label}</strong>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
      {previewDocument && (
        <div className="clinical-workspace">
          <header>
            <div>
              <strong>{patient.anonymousPatientId}</strong>
              <span>{previewDocument.filename}</span>
            </div>
            <button
              onClick={() => {
                setPreviewDocument(null);
              }}
            >
              <XCircle />
              Close
            </button>
          </header>
          <div className="clinical-workspace-body preview-only">
            <aside className="clinical-files-pane">
              <div className="clinical-file-tabs">
                {documents
                  .filter((item) => item.status === "READY")
                  .map((document) => (
                    <button
                      className={
                        previewDocument?.id === document.id ? "active" : ""
                      }
                      key={document.id}
                      onClick={() => setPreviewDocument(document)}
                    >
                      <FileText />
                      {document.documentType}
                      <small>{document.filename}</small>
                    </button>
                  ))}
              </div>
              {previewDocument ? (
                <iframe
                  className="document-frame"
                  title={previewDocument.filename}
                  src={`/api/v2/documents/${encodeURIComponent(previewDocument.id)}/file`}
                />
              ) : (
                <div className="preview-empty">
                  Select an attached clinical file to preview it.
                </div>
              )}
            </aside>
          </div>
        </div>
      )}
      {downloadOpen && (
        <div
          className="platform-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="download-title"
        >
          <section className="platform-modal">
            <div className="section-heading">
              <div>
                <span>Secure export</span>
                <h2 id="download-title">Download Patient Package</h2>
              </div>
              <button
                className="icon-action"
                aria-label="Close"
                onClick={() => setDownloadOpen(false)}
              >
                <XCircle />
              </button>
            </div>
            {resources.map((resource) => {
              const key = `${resource.type}:${resource.id}`;
              return (
                <label className="download-choice" key={key}>
                  <input
                    type="checkbox"
                    checked={selected.includes(key)}
                    onChange={(e) =>
                      setSelected((current) =>
                        e.target.checked
                          ? [...current, key]
                          : current.filter((item) => item !== key),
                      )
                    }
                  />
                  <span>{resource.label}</span>
                </label>
              );
            })}
            {download?.status && (
              <div className="platform-success">
                <FileClock />
                {download.status === "READY"
                  ? "Package ready"
                  : "Preparing anonymized package…"}
              </div>
            )}
            <div className="modal-actions">
              {download?.url ? (
                <a className="primary-button" href={download.url}>
                  Download ZIP
                </a>
              ) : (
                <button
                  className="primary-button"
                  disabled={
                    !selected.length ||
                    ["QUEUED", "PROCESSING"].includes(download?.status)
                  }
                  onClick={generate}
                >
                  <Download size={16} />
                  Generate ZIP
                </button>
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

async function uploadWithConcurrency(tasks, concurrency, onComplete) {
  let cursor = 0;
  let completed = 0;
  const workers = Array.from(
    { length: Math.min(concurrency, tasks.length) },
    async () => {
      while (cursor < tasks.length) {
        const task = tasks[cursor++];
        await task();
        completed += 1;
        onComplete(completed, tasks.length);
      }
    },
  );
  await Promise.all(workers);
}

function uploadFilePath(file) {
  return file.relativePath || file.webkitRelativePath || file.name;
}
function imagingContentType(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
  return file.type || "application/dicom";
}
function uploadFingerprint(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}
function clientRequestId() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `web-${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`
  );
}
async function expandImagingFiles(selected) {
  const expanded = [];
  for (const file of selected) {
    if (!file.name.toLowerCase().endsWith(".zip")) {
      expanded.push(file);
      continue;
    }
    const { default: JSZip } = await import("jszip");
    const archive = await JSZip.loadAsync(file);
    const entries = Object.values(archive.files).filter(
      (entry) =>
        !entry.dir &&
        !entry.name.startsWith("__MACOSX/") &&
        !entry.name.endsWith(".DS_Store"),
    );
    for (const entry of entries) {
      const blob = await entry.async("blob");
      const name = entry.name.split("/").pop() || "image.dcm";
      const next = new File([blob], name, {
        type: imagingContentType({ name, type: "" }),
        lastModified: file.lastModified,
      });
      Object.defineProperty(next, "relativePath", {
        value: `${file.name.replace(/\.zip$/i, "")}/${entry.name}`,
      });
      expanded.push(next);
    }
  }
  if (!expanded.length)
    throw new Error(
      "The selected ZIP or folder does not contain any imaging files.",
    );
  return expanded;
}

function UploadWizard({
  request,
  hospitals,
  preset,
  inline = false,
  onComplete,
}) {
  const [form, setForm] = useState({
    hospitalId: "",
    programId: "",
    patientId: "",
    patientMode: "new",
    uploadType: "CT",
    originalFolderName: "",
    ...(preset || {}),
  });
  const [files, setFiles] = useState([]);
  const [patients, setPatients] = useState([]);
  const [progress, setProgress] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const hospital = hospitals.find((item) => item.id === form.hospitalId);
  const isImaging = ["CT", "XRAY", "IMAGING"].includes(form.uploadType);
  useEffect(() => {
    if (hospitals.length === 1 && !form.hospitalId)
      setForm((current) => ({ ...current, hospitalId: hospitals[0].id }));
  }, [hospitals, form.hospitalId]);
  useEffect(() => {
    if (hospital?.programs?.length === 1 && !form.programId)
      setForm((current) => ({
        ...current,
        programId: hospital.programs[0].id,
      }));
  }, [hospital, form.programId]);
  useEffect(() => {
    if (!form.hospitalId) return;
    request(`/patients?hospitalId=${form.hospitalId}&limit=200`).then((body) =>
      setPatients(body.records),
    );
  }, [form.hospitalId, request]);
  const selectFiles = async (selected) => {
    try {
      setError("");
      const next = isImaging ? await expandImagingFiles(selected) : selected;
      if (
        isImaging &&
        next.some((file) => !/[.](dcm|dicom|png|jpe?g)$/i.test(file.name))
      )
        throw new Error(
          "Imaging uploads accept DICOM, PNG, JPG, and JPEG files only.",
        );
      setFiles(next);
      const firstPath = uploadFilePath(next[0] || {});
      setForm((current) => ({
        ...current,
        originalFolderName: firstPath.split("/")[0] || next[0]?.name || "",
      }));
    } catch (e) {
      setFiles([]);
      setError(e.message);
    }
  };
  const start = async () => {
    let resumeKey = "";
    try {
      setError("");
      if (!form.hospitalId || !form.programId || !files.length)
        throw new Error("Choose a hospital, program, and files.");
      if (!isImaging && files.length !== 1)
        throw new Error("Choose exactly one report or document.");
      let patientId = form.patientId;
      let anonymousPatientId = patients.find(
        (p) => p.id === patientId,
      )?.anonymousPatientId;
      if (form.patientMode === "new") {
        const patient = await request("/patients", {
          method: "POST",
          body: JSON.stringify({
            hospitalId: form.hospitalId,
            programId: form.programId,
            originalFolderName: form.originalFolderName,
          }),
        });
        patientId = patient.id;
        anonymousPatientId = patient.anonymousPatientId;
      }
      if (!patientId) throw new Error("Choose an existing patient.");
      const fingerprint = [
        patientId,
        form.uploadType,
        ...files.map(
          (file) => `${uploadFilePath(file)}:${file.size}:${file.lastModified}`,
        ),
      ].join("|");
      resumeKey = `dectrocel-upload:${uploadFingerprint(fingerprint)}`;
      let session = JSON.parse(localStorage.getItem(resumeKey) || "null");
      if (!session) {
        const idempotency = clientRequestId();
        session = await request("/uploads", {
          method: "POST",
          headers: { "Idempotency-Key": idempotency },
          body: JSON.stringify({
            ...form,
            patientId,
            filename: files[0]?.name,
            totalFiles: files.length,
            totalBytes: files.reduce((n, f) => n + f.size, 0),
          }),
        });
        localStorage.setItem(resumeKey, JSON.stringify(session));
      }
      setProgress({
        done: 0,
        total: files.length,
        phase: "Uploading securely",
      });
      const tasks = files.map((file) => async () => {
        const partSize = 8 * 1024 * 1024;
        const partCount = Math.max(1, Math.ceil(file.size / partSize));
        const objectName = uploadFilePath(file);
        const init = await request(`/uploads/${session.id}/multipart`, {
          method: "POST",
          body: JSON.stringify({
            objectName,
            partCount,
            contentType: isImaging
              ? imagingContentType(file)
              : file.type || "application/octet-stream",
          }),
        });
        const parts = [...(init.completedParts || [])];
        const completed = new Set(parts.map((part) => part.partNumber));
        for (const part of init.parts) {
          if (completed.has(part.partNumber)) continue;
          const start = (part.partNumber - 1) * partSize;
          let response;
          for (let attempt = 1; attempt <= 3; attempt += 1) {
            response = await fetch(part.url, {
              method: "PUT",
              body: file.slice(start, Math.min(start + partSize, file.size)),
            });
            if (response.ok) break;
          }
          if (!response?.ok)
            throw new Error(
              `A file part failed (${response?.status || "network"}).`,
            );
          parts.push({
            partNumber: part.partNumber,
            etag: response.headers.get("etag"),
          });
        }
        await request(`/uploads/${session.id}/multipart/complete`, {
          method: "POST",
          body: JSON.stringify({
            objectName,
            uploadId: init.uploadId,
            parts,
            sizeBytes: file.size,
          }),
        });
      });
      await uploadWithConcurrency(tasks, 8, (done, total) =>
        setProgress({ done, total, phase: "Uploading securely" }),
      );
      setProgress((p) => ({
        ...p,
        phase: isImaging ? "Queueing imaging processing" : "Validating document",
      }));
      await request(`/uploads/${session.id}/finalize`, {
        method: "POST",
        body: JSON.stringify({
          manifest: files.map((file) => ({
            name: uploadFilePath(file),
            size: file.size,
          })),
        }),
      });
      localStorage.removeItem(resumeKey);
      setMessage(
        `Upload received for ${anonymousPatientId}. Secure background processing is now running.`,
      );
      setFiles([]);
      setProgress(null);
      onComplete?.(session);
    } catch (e) {
      if (resumeKey && e.message.includes("no longer active"))
        localStorage.removeItem(resumeKey);
      setError(e.message);
      setProgress(null);
    }
  };
  useEffect(() => {
    const warn = (e) => {
      if (progress) {
        e.preventDefault();
        e.returnValue = "A study upload is currently in progress.";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [progress]);
  return (
    <>
      {!inline && (
        <PageHeader
          eyebrow="Resumable direct-to-storage"
          title="New patient data upload"
          description="Create one anonymous patient or attach imaging and reports to an existing profile."
        />
      )}
      <ErrorNotice message={error} />
      {message && (
        <div className="platform-success">
          <CheckCircle2 />
          {message}
        </div>
      )}
      <section
        className={`platform-section upload-workflow ${inline ? "inline-upload-workflow" : ""}`}
      >
        {!inline && (
          <div className="form-grid">
            {hospitals.length > 1 && (
              <label>
                Hospital
                <select
                  value={form.hospitalId}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      hospitalId: e.target.value,
                      programId: "",
                      patientId: "",
                    })
                  }
                >
                  <option value="">Select hospital</option>
                  {hospitals.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {hospital && (
              <label>
                Program
                <select
                  value={form.programId}
                  required
                  onChange={(e) =>
                    setForm({ ...form, programId: e.target.value })
                  }
                >
                  <option value="">Select program</option>
                  {(hospital?.programs || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Patient workflow
              <select
                value={form.patientMode}
                onChange={(e) =>
                  setForm({
                    ...form,
                    patientMode: e.target.value,
                    patientId: "",
                  })
                }
              >
                <option value="new">Create new patient</option>
                <option value="existing">Add to existing patient</option>
              </select>
            </label>
            {form.patientMode === "existing" && (
              <label>
                Patient
                <select
                  value={form.patientId}
                  onChange={(e) =>
                    setForm({ ...form, patientId: e.target.value })
                  }
                >
                  <option value="">Select patient</option>
                  {patients.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.anonymousPatientId}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Type
              <select
                value={form.uploadType}
                onChange={(e) => {
                  setFiles([]);
                  setForm({ ...form, uploadType: e.target.value });
                }}
              >
                <option>CT</option>
                <option value="XRAY">X-ray</option>
                <option value="IMAGING">Other imaging</option>
                <option value="RADIOLOGY">Radiology report</option>
                <option value="HISTOPATHOLOGY">Histopathology report</option>
                <option value="DOCUMENT">Supporting document</option>
              </select>
            </label>
          </div>
        )}
        {isImaging ? (
          <div className="file-drop imaging-file-drop">
            <Upload />
            <strong>
              Select DICOM, PNG, JPEG, a folder, or a ZIP archive
            </strong>
            <span>
              {files.length
                ? `${files.length} imaging files ready`
                : "DICOM and raster images in nested folders or ZIP archives are supported"}
            </span>
            <div className="file-source-actions">
              <label className="secondary-button">
                Choose files or ZIP
                <input
                  key={`${form.uploadType}-files`}
                  type="file"
                  multiple
                  accept=".dcm,.dicom,.png,.jpg,.jpeg,.zip,application/dicom,image/png,image/jpeg,application/zip"
                  onChange={(e) =>
                    selectFiles(Array.from(e.target.files || []))
                  }
                />
              </label>
              <label className="secondary-button">
                Choose folder
                <input
                  key={`${form.uploadType}-folder`}
                  type="file"
                  multiple
                  webkitdirectory=""
                  onChange={(e) =>
                    selectFiles(Array.from(e.target.files || []))
                  }
                />
              </label>
            </div>
          </div>
        ) : (
          <label className="file-drop">
            <Upload />
            <strong>Select one PDF, DOCX, text, PNG, or JPEG file</strong>
            <span>
              {files.length
                ? `${files.length} file selected`
                : "Files upload directly to private temporary storage"}
            </span>
            <input
              key={form.uploadType}
              type="file"
              accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/png,image/jpeg"
              onChange={(e) => selectFiles(Array.from(e.target.files || []))}
            />
          </label>
        )}
        <button
          className="primary-button"
          disabled={Boolean(progress) || !files.length}
          onClick={start}
        >
          {progress ? <LoaderCircle className="spin" /> : <ShieldCheck />}Attach
          to patient
        </button>
      </section>
      {progress && (
        <div className="upload-overlay" role="dialog" aria-modal="true">
          <div>
            <LoaderCircle className="spin" />
            <h2>{progress.phase}</h2>
            <p>
              {progress.done} / {progress.total} files
            </p>
            <progress value={progress.done} max={progress.total} />
            <small>
              Please keep this window open until receipt is confirmed.
            </small>
          </div>
        </div>
      )}
    </>
  );
}

function GenericList({ request, endpoint, title, eyebrow, columns }) {
  const [data, setData] = useState();
  const [error, setError] = useState("");
  const load = useCallback(
    () =>
      request(endpoint)
        .then(setData)
        .catch((e) => setError(e.message)),
    [endpoint, request],
  );
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description="Authorized operational records with tenant isolation."
        action={
          <button className="secondary-button" onClick={load}>
            <RefreshCw size={16} />
            Refresh
          </button>
        }
      />
      <ErrorNotice message={error} />
      {!data ? (
        <Loading />
      ) : (
        <div className="platform-table-wrap">
          <table className="platform-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.records || []).map((row, i) => (
                <tr key={row.id || i}>
                  {columns.map((c) => (
                    <td key={c.key}>
                      {c.render ? c.render(row) : (row[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function DownloadsView({ request }) {
  const [data, setData] = useState({ records: [] });
  const [links, setLinks] = useState({});
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const body = await request("/downloads");
      setData(body);
      const ready = body.records.filter((row) => row.status === "READY");
      const resolved = await Promise.all(
        ready.map(async (row) => [
          row.id,
          await request(`/downloads/${row.id}`),
        ]),
      );
      setLinks(Object.fromEntries(resolved));
    } catch (e) {
      setError(e.message);
    }
  }, [request]);
  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load]);
  return (
    <>
      <PageHeader
        eyebrow="Secure exports"
        title="Downloads"
        description="Asynchronous, selective anonymized patient packages."
        action={
          <button className="secondary-button" onClick={load}>
            <RefreshCw size={16} />
            Refresh
          </button>
        }
      />
      <ErrorNotice message={error} />
      <div className="platform-table-wrap">
        <table className="platform-table">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Hospital</th>
              <th>Status</th>
              <th>Requested</th>
              <th>Expires</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.records.map((row) => (
              <tr key={row.id}>
                <td>
                  <strong>{row.anonymousPatientId}</strong>
                </td>
                <td>{row.hospitalName}</td>
                <td>
                  <span className={badge(row.status)}>{row.status}</span>
                </td>
                <td>{formatDate(row.createdAt)}</td>
                <td>{formatDate(row.expiresAt)}</td>
                <td>
                  {links[row.id]?.url && (
                    <a className="secondary-button" href={links[row.id].url}>
                      Download ZIP
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function UsersView({ request, hospitals }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    hospitalId: hospitals[0]?.id || "",
    displayName: "",
    email: "",
    password: "",
    role: "HOSPITAL_USER",
  });
  const [error, setError] = useState("");
  const load = useCallback(
    () =>
      request("/users")
        .then((body) => setRows(body.records))
        .catch((e) => setError(e.message)),
    [request],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const create = async () => {
    try {
      await request("/users", { method: "POST", body: JSON.stringify(form) });
      setOpen(false);
      setForm((current) => ({
        ...current,
        displayName: "",
        email: "",
        password: "",
      }));
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const deactivate = async (row) => {
    if (!window.confirm(`Remove ${row.email} from ${row.hospitalName}?`))
      return;
    try {
      await request(`/users/${row.id}`, {
        method: "PATCH",
        body: JSON.stringify({ hospitalId: row.hospitalId, active: false }),
      });
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="Access management"
        title="Hospital users"
        description="Create and remove tenant-scoped accounts with auditable role assignments."
        action={
          <button className="primary-button" onClick={() => setOpen(true)}>
            <Plus size={16} />
            Add user
          </button>
        }
      />
      <ErrorNotice message={error} />
      <div className="platform-table-wrap">
        <table className="platform-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Hospital</th>
              <th>Role</th>
              <th>Last login</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.id}:${row.hospitalId}`}>
                <td>{row.displayName}</td>
                <td>{row.email}</td>
                <td>{row.hospitalName}</td>
                <td>{row.role}</td>
                <td>{formatDate(row.lastLoginAt)}</td>
                <td>
                  <button
                    className="secondary-button"
                    onClick={() => deactivate(row)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="platform-modal-backdrop">
          <section className="platform-modal" role="dialog" aria-modal="true">
            <div className="section-heading">
              <div>
                <span>Tenant access</span>
                <h2>Add hospital user</h2>
              </div>
              <button className="icon-action" onClick={() => setOpen(false)}>
                <XCircle />
              </button>
            </div>
            <div className="form-grid">
              <label>
                Hospital
                <select
                  value={form.hospitalId}
                  onChange={(e) =>
                    setForm({ ...form, hospitalId: e.target.value })
                  }
                >
                  {hospitals.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Role
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                >
                  <option value="HOSPITAL_USER">Hospital user</option>
                  <option value="HOSPITAL_ADMIN">Hospital admin</option>
                </select>
              </label>
              <label>
                Name
                <input
                  value={form.displayName}
                  onChange={(e) =>
                    setForm({ ...form, displayName: e.target.value })
                  }
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  minLength="12"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                />
              </label>
            </div>
            <div className="modal-actions">
              <button className="primary-button" onClick={create}>
                Create user
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function ProcessingView({ request }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [error, setError] = useState("");
  const load = useCallback(
    () =>
      request("/processing")
        .then((body) => setRows(body.records))
        .catch((e) => setError(e.message)),
    [request],
  );
  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load]);
  const retry = async (id) => {
    try {
      await request(`/processing/${id}/retry`, { method: "POST", body: "{}" });
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const visible =
    filter === "ALL" ? rows : rows.filter((row) => row.status === filter);
  return (
    <>
      <PageHeader
        eyebrow="Background operations"
        title="Processing monitor"
        description="Durable upload, anonymization, document, cleanup, and download jobs."
        action={
          <button className="secondary-button" onClick={load}>
            <RefreshCw size={16} />
            Refresh
          </button>
        }
      />
      <div className="platform-toolbar">
        <label>
          Status
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option>ALL</option>
            <option>FAILED</option>
            <option>PROCESSING</option>
            <option>QUEUED</option>
            <option>COMPLETED</option>
          </select>
        </label>
      </div>
      <ErrorNotice message={error} />
      <div className="platform-table-wrap">
        <table className="platform-table">
          <thead>
            <tr>
              <th>Job ID</th>
              <th>Hospital</th>
              <th>Patient</th>
              <th>Type</th>
              <th>Progress</th>
              <th>Status</th>
              <th>Started</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id}>
                <td>
                  <small>{row.id}</small>
                </td>
                <td>{row.hospitalName}</td>
                <td>{row.anonymousPatientId || "System"}</td>
                <td>{row.jobType}</td>
                <td>{row.progress}%</td>
                <td>
                  <span className={badge(row.status)}>{row.status}</span>
                </td>
                <td>{formatDate(row.startedAt)}</td>
                <td>
                  {row.status === "FAILED" && (
                    <button
                      className="secondary-button"
                      onClick={() => retry(row.id)}
                    >
                      Retry
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function PlatformApp() {
  const { identity, request } = usePlatformApi();
  const [active, setActive] = useState("dashboard");
  const [patientId, setPatientId] = useState(null);
  const [uploadPreset, setUploadPreset] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const loadHospitals = useCallback(
    () => request("/hospitals").then((body) => setHospitals(body.records)),
    [request],
  );
  useEffect(() => {
    if (identity?.id) loadHospitals();
  }, [identity?.id, loadHospitals]);
  if (!identity) return <Loading />;
  if (identity.error) return <ErrorNotice message={identity.error} />;
  const visibleNav = identity.isSuperAdmin
    ? navigation
    : [
        ["dashboard", "Dashboard", LayoutDashboard],
        ["upload", "Upload", Upload],
        ["patients", "Studies", Database],
      ];
  const content = () => {
    if (patientId)
      return (
        <PatientProfile
          id={patientId}
          request={request}
          onBack={() => setPatientId(null)}
          onUpload={(preset) => {
            setUploadPreset(preset);
            setPatientId(null);
            setActive("upload");
          }}
        />
      );
    if (active === "dashboard")
      return (
        <Dashboard
          request={request}
          openPatient={setPatientId}
          navigate={setActive}
          compactNavigation={!identity.isSuperAdmin}
        />
      );
    if (active === "patients")
      return <Patients request={request} openPatient={setPatientId} />;
    if (active === "upload")
      return (
        <UploadWizard
          request={request}
          hospitals={hospitals}
          preset={uploadPreset}
        />
      );
    if (active === "downloads") return <DownloadsView request={request} />;
    if (active === "processing") return <ProcessingView request={request} />;
    if (active === "activity")
      return (
        <GenericList
          request={request}
          endpoint="/audit"
          title="Audit activity"
          eyebrow="Accountability"
          columns={[
            {
              key: "occurredAt",
              label: "Time",
              render: (r) => formatDate(r.occurredAt),
            },
            { key: "action", label: "Action" },
            { key: "resourceType", label: "Resource" },
            {
              key: "result",
              label: "Result",
              render: (r) => (
                <span className={badge(r.result)}>{r.result}</span>
              ),
            },
            { key: "requestId", label: "Request ID" },
          ]}
        />
      );
    if (active === "users")
      return <UsersView request={request} hospitals={hospitals} />;
    if (active === "health") return <Health request={request} />;
    if (active === "hospitals")
      return (
        <Hospitals
          hospitals={hospitals}
          request={request}
          refresh={loadHospitals}
        />
      );
  };
  const initials =
    identity.displayName
      ?.split(/\s+/)
      .map((part) => part[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "DC";
  return (
    <div className="platform-shell">
      <aside className="platform-sidebar">
        <div className="platform-brand">
          <img src="/dectrocel-brand.jpeg" alt="Dectrocel" />
        </div>
        <nav>
          {visibleNav.map(([id, label, Icon]) => (
            <button
              key={id}
              className={active === id && !patientId ? "active" : ""}
              onClick={() => {
                setPatientId(null);
                setActive(id);
              }}
            >
              <Icon size={19} />
              {label}
            </button>
          ))}
        </nav>
        <form method="post" action="/logout">
          <button>
            <LogOut size={19} />
            Logout
          </button>
        </form>
      </aside>
      <main className="platform-main">
        <div className="platform-topbar">
          <img
            src="/login-institutions.png"
            alt="Partner healthcare institutions"
          />
          <div className="account-summary">
            <b>{initials}</b>
            <span>
              <strong>{identity.displayName}</strong>
              <small>
                {identity.isSuperAdmin
                  ? "Dectrocel Super Admin"
                  : identity.memberships[0]?.hospitalName}
              </small>
            </span>
            <ChevronRight size={16} />
          </div>
        </div>
        <div className="platform-content">{content()}</div>
      </main>
    </div>
  );
}

function Health({ request }) {
  const [data, setData] = useState();
  useEffect(() => {
    request("/system-health").then(setData);
  }, [request]);
  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="System health"
        description="Non-secret connectivity and processing indicators."
      />
      {!data ? (
        <Loading />
      ) : (
        <div className="platform-metrics">
          {[
            ["Database", data.database, Database],
            ["Private S3", data.s3, ShieldCheck],
            ["Health check", `${data.durationMs} ms`, HeartPulse],
          ].map(([l, v, I]) => (
            <article key={l}>
              <I />
              <span>{l}</span>
              <strong>{v}</strong>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
function Hospitals({ hospitals, request, refresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "" });
  const [programs, setPrograms] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    request("/programs")
      .then((body) => setPrograms(body.records))
      .catch((e) => setError(e.message));
  }, [request]);
  const create = async () => {
    try {
      const hospital = await request("/hospitals", {
        method: "POST",
        body: JSON.stringify(form),
      });
      const ncg = programs.find((p) => p.code === "NCG");
      if (ncg)
        await request(`/hospitals/${hospital.id}/programs/${ncg.id}`, {
          method: "PUT",
          body: JSON.stringify({ active: true }),
        });
      setOpen(false);
      setForm({ code: "", name: "" });
      refresh();
    } catch (e) {
      setError(e.message);
    }
  };
  const toggle = async (hospital, program) => {
    try {
      const active = hospital.programs.some((item) => item.id === program.id);
      await request(`/hospitals/${hospital.id}/programs/${program.id}`, {
        method: "PUT",
        body: JSON.stringify({ active: !active }),
      });
      refresh();
    } catch (e) {
      setError(e.message);
    }
  };
  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Hospitals & programs"
        description="Programs are enabled per institution without hospital-specific application logic."
        action={
          <button className="primary-button" onClick={() => setOpen(true)}>
            <Plus size={16} />
            Add hospital
          </button>
        }
      />
      <ErrorNotice message={error} />
      <div className="hospital-grid">
        {hospitals.map((h) => (
          <article key={h.id}>
            <Building2 />
            <h2>{h.name}</h2>
            <span>{h.code}</span>
            <div>
              {programs.map((p) => (
                <button
                  className={
                    h.programs.some((item) => item.id === p.id)
                      ? "program-toggle active"
                      : "program-toggle"
                  }
                  key={p.id}
                  onClick={() => toggle(h, p)}
                >
                  {p.code}
                </button>
              ))}
            </div>
          </article>
        ))}
      </div>
      {open && (
        <div className="platform-modal-backdrop">
          <section className="platform-modal">
            <div className="section-heading">
              <div>
                <span>Tenant setup</span>
                <h2>Add hospital</h2>
              </div>
              <button className="icon-action" onClick={() => setOpen(false)}>
                <XCircle />
              </button>
            </div>
            <div className="form-grid">
              <label>
                Code
                <input
                  value={form.code}
                  maxLength="20"
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                />
              </label>
              <label>
                Hospital name
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
            </div>
            <div className="modal-actions">
              <button className="primary-button" onClick={create}>
                Create hospital
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
